import { io, Socket } from 'socket.io-client';
import { localStorageService } from './local-storage.service';

export interface WebSocketConfig {
  url: string;
  namespace: string;
  token: string;
}

export interface ConnectionStatus {
  connected: boolean;
  reconnecting: boolean;
  retryCount: number;
  maxRetries: number;
  error?: string;
}

export interface RunOrderUpdateData {
  eventId: string;
  showId: string;
  ring: string;
  completedDogs: string[];
  classId: string;
  timestamp: string;
  reload?: boolean; // Signal to reload all data from database
}

export interface RoomData {
  eventId: string;
  showId: string;
  ring: string;
}

export type ConnectionStatusCallback = (status: ConnectionStatus) => void;
export type RunOrderUpdateCallback = (data: RunOrderUpdateData) => void;
export type ErrorCallback = (error: string) => void;

/**
 * WebSocket service for real-time run order updates
 * SECURITY: Handles JWT authentication and secure connections
 * MEMORY LEAK PREVENTION: Proper cleanup and connection management
 */
export class WebSocketService {
  private socket: Socket | null = null;
  private config: WebSocketConfig | null = null;
  private connectionStatus: ConnectionStatus = {
    connected: false,
    reconnecting: false,
    retryCount: 0,
    maxRetries: 5,
  };

  // Callback storage
  private connectionStatusCallbacks: Set<ConnectionStatusCallback> = new Set();
  private runOrderUpdateCallbacks: Set<RunOrderUpdateCallback> = new Set();
  private errorCallbacks: Set<ErrorCallback> = new Set();
  private customEventListeners: Map<string, Set<(data: any) => void>> = new Map();

  // Reconnection management
  private reconnectTimer: NodeJS.Timeout | null = null;
  private reconnectDelay = 2000; // 2 seconds initial delay

  // Room tracking for cleanup - track ALL rooms, not just one
  private joinedRooms: Set<string> = new Set();

  /**
   * Get a fresh JWT token, attempting to refresh if needed
   * SECURITY: Ensures we always use a valid token for WebSocket connections
   */
  private async getFreshToken(): Promise<string | null> {
    try {
      // Get the current token from localStorage first
      let token = localStorageService.getItem(localStorageService.ACCESS_TOKEN);
      
      if (!token) {
        return null;
      }
      
      // Check if token is expired or about to expire (within 5 minutes)
      try {
        const parts = token.split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]));
          const expiresAt = payload.exp * 1000; // Convert to milliseconds
          const now = Date.now();
          
          if (now >= expiresAt) {
            return null;
          }
        }
      } catch (error) {
        // Continue anyway - let the backend reject if invalid
      }
      
      return token;
      
    } catch (error) {
      return null;
    }
  }

  /**
   * Initialize WebSocket connection
   * SECURITY: Uses fresh JWT token for authentication
   * MULTI-COMPONENT: Only creates a new connection if not already connected
   */
  async connect(config: WebSocketConfig): Promise<void> {
    try {
      // If already connected to the same namespace, reuse the connection
      if (this.socket && this.socket.connected && this.config?.namespace === config.namespace) {
        return;
      }

      // Prevent multiple connections - disconnect any existing connection first
      if (this.socket) {
        this.socket.removeAllListeners();
        this.socket.disconnect();
        this.socket = null;
      }

      // Get a fresh token instead of using the potentially stale one from config
      const freshToken = await this.getFreshToken();
      if (!freshToken) {
        this.handleConnectionError('Failed to obtain valid authentication token');
        return;
      }

      // Update config with fresh token
      this.config = { ...config, token: freshToken };

      // Create socket with fresh authentication
      const connectionUrl = config.url + config.namespace;
      
      this.socket = io(connectionUrl, {
        auth: {
          token: freshToken,
        },
        transports: ['websocket', 'polling'], // Fallback to polling if WebSocket fails
        timeout: 10000, // 10 second connection timeout
        reconnection: false, // We handle reconnection manually for better control
      });

      this.setupEventListeners();

    } catch (error) {
      this.handleConnectionError('Failed to initialize connection');
    }
  }

  /**
   * Setup WebSocket event listeners
   * MEMORY LEAK PREVENTION: Removes old listeners before adding new ones
   */
  private setupEventListeners(): void {
    if (!this.socket) return;
    
    // Remove any existing listeners to prevent memory leaks
    this.socket.removeAllListeners();

    // Connection events
    this.socket.on('connect', () => {
      // DON'T set connected=true yet - wait for connection_status from backend
    });

    this.socket.on('disconnect', (reason: string) => {
      this.connectionStatus = {
        ...this.connectionStatus,
        connected: false,
      };
      this.notifyConnectionStatus();

      // Start reconnection if not manually disconnected
      if (reason !== 'io client disconnect') {
        this.startReconnection();
      }
    });

    this.socket.on('connect_error', (error: any) => {
      this.handleConnectionError(`Connection failed: ${error.message || error.type || 'Unknown error'}`);
    });

    // Application-specific events
    this.socket.on('connection_status', (data: any) => {
      if (data.connected) {
        // NOW we're truly authenticated and ready
        this.connectionStatus = {
          connected: true,
          reconnecting: false,
          retryCount: 0,
          maxRetries: 5,
        };
        this.clearReconnectTimer();
        this.notifyConnectionStatus();
      } else {
        this.handleConnectionError(data.message || 'Server disconnected');
      }
    });

    this.socket.on('room_joined', (data: any) => {
      // Room joined successfully
    });

    this.socket.on('run_order_update', (data: RunOrderUpdateData) => {
      this.notifyRunOrderUpdate(data);
    });

    this.socket.on('room_left', (data: any) => {
      // Room left
    });

    this.socket.on('error', (error: any) => {
      this.notifyError(error.message || 'Unknown server error');
    });

    // Re-register any custom event listeners that were added before connection
    this.customEventListeners.forEach((callbacks, eventName) => {
      callbacks.forEach(callback => {
        this.socket!.on(eventName, callback);
      });
    });
  }

  /**
   * Join a run order room for real-time updates
   * SECURITY: Server validates permissions before allowing room join
   */
  joinRunOrderRoom(roomData: RoomData): void {
    if (!this.socket?.connected) {
      this.notifyError('Not connected to WebSocket server');
      return;
    }

    // Create a unique room identifier
    const roomKey = `show_${roomData.showId}_ring_${roomData.ring}`;
    
    // Track this room for cleanup
    this.joinedRooms.add(roomKey);

    this.socket.emit('join_run_order_room', roomData);
  }

  /**
   * Leave all joined run order rooms
   * MEMORY LEAK PREVENTION: Properly clean up ALL room subscriptions
   */
  leaveRunOrderRoom(): void {
    if (!this.socket?.connected || this.joinedRooms.size === 0) {
      return;
    }
    
    // Leave all rooms we joined
    this.joinedRooms.forEach((roomKey) => {
      // Parse the room key back to room data format
      const parts = roomKey.match(/^show_(.+)_ring_(.+)$/);
      if (parts) {
        const roomData = {
          eventId: '', // We don't have eventId in the key, but the server doesn't strictly need it for leaving
          showId: parts[1],
          ring: parts[2],
        };
        this.socket!.emit('leave_run_order_room', roomData);
      }
    });
    
    this.joinedRooms.clear();
  }

  /**
   * Handle connection errors and start reconnection process
   */
  private handleConnectionError(error: string): void {
    
    this.connectionStatus = {
      ...this.connectionStatus,
      connected: false,
      error,
    };
    
    this.notifyConnectionStatus();
    this.notifyError(error);
    
    this.startReconnection();
  }

  /**
   * Start reconnection process with exponential backoff
   * INFINITE LOOP PREVENTION: Limited retries with increasing delays
   */
  private startReconnection(): void {
    if (this.reconnectTimer) {
      return;
    }

    this.connectionStatus = {
      ...this.connectionStatus,
      reconnecting: true,
      retryCount: this.connectionStatus.retryCount + 1,
    };

    this.notifyConnectionStatus();

    // Exponential backoff capped at 30s: 2s, 4s, 8s, 16s, 30s, 30s, ...
    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.connectionStatus.retryCount - 1), 30000);
    


    this.reconnectTimer = setTimeout(async () => {
      this.reconnectTimer = null;
      
      if (this.config) {
        await this.connect(this.config);
      }
    }, delay);
  }

  /**
   * Manually retry connection (called by user action)
   * Resets retry count to give user fresh attempts
   */
  async retryConnection(): Promise<void> {
    // Reset retry count for manual attempts
    this.connectionStatus = {
      ...this.connectionStatus,
      retryCount: 0,
      error: undefined,
    };

    this.clearReconnectTimer();
    
    if (this.config) {
      await this.connect(this.config);
    } else {
      this.notifyError('No connection configuration available');
    }
  }

  /**
   * Clear reconnection timer
   * MEMORY LEAK PREVENTION: Clean up timers
   */
  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * Disconnect and cleanup
   * MEMORY LEAK PREVENTION: Complete cleanup of all resources
   */
  disconnect(): void {


    // Clear timers
    this.clearReconnectTimer();

    // Leave current room
    this.leaveRunOrderRoom();

    // Disconnect socket
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    // Reset state
    this.config = null;
    this.joinedRooms.clear();
    this.connectionStatus = {
      connected: false,
      reconnecting: false,
      retryCount: 0,
      maxRetries: 5,
    };

    // Clear all callbacks to prevent memory leaks
    this.connectionStatusCallbacks.clear();
    this.runOrderUpdateCallbacks.clear();
    this.errorCallbacks.clear();
    this.customEventListeners.clear();
  }

  // Callback management methods
  onConnectionStatus(callback: ConnectionStatusCallback): () => void {
    this.connectionStatusCallbacks.add(callback);
    
    // If already connected, immediately invoke so late-mounting components don't miss the event
    if (this.connectionStatus.connected) {
      callback(this.connectionStatus);
    }
    
    // Return unsubscribe function to prevent memory leaks
    return () => {
      this.connectionStatusCallbacks.delete(callback);
    };
  }

  onRunOrderUpdate(callback: RunOrderUpdateCallback): () => void {
    this.runOrderUpdateCallbacks.add(callback);
    
    return () => {
      this.runOrderUpdateCallbacks.delete(callback);
    };
  }

  onError(callback: ErrorCallback): () => void {
    this.errorCallbacks.add(callback);
    
    return () => {
      this.errorCallbacks.delete(callback);
    };
  }

  /**
   * Listen to a custom WebSocket event
   * Returns an unsubscribe function
   */
  onCustomEvent(eventName: string, callback: (data: any) => void): () => void {
    // Store the callback for this event
    if (!this.customEventListeners.has(eventName)) {
      this.customEventListeners.set(eventName, new Set());
    }
    this.customEventListeners.get(eventName)!.add(callback);
    
    // If socket is already connected, register the listener now
    if (this.socket) {
      this.socket.on(eventName, callback);
    }
    
    // Return unsubscribe function
    return () => {
      const callbacks = this.customEventListeners.get(eventName);
      if (callbacks) {
        callbacks.delete(callback);
        if (callbacks.size === 0) {
          this.customEventListeners.delete(eventName);
        }
      }
      
      if (this.socket) {
        this.socket.off(eventName, callback);
      }
    };
  }

  // Notification methods
  private notifyConnectionStatus(): void {
    this.connectionStatusCallbacks.forEach(callback => {
      try {
        callback(this.connectionStatus);
      } catch (error) {
      }
    });
  }

  private notifyRunOrderUpdate(data: RunOrderUpdateData): void {
    this.runOrderUpdateCallbacks.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
      }
    });
  }

  private notifyError(error: string): void {
    this.errorCallbacks.forEach(callback => {
      try {
        callback(error);
      } catch (error) {
      }
    });
  }

  // Getters
  get isConnected(): boolean {
    return this.connectionStatus.connected;
  }

  get status(): ConnectionStatus {
    return { ...this.connectionStatus };
  }

  /**
   * Get all joined rooms
   * Used for debugging and monitoring
   */
  get currentRoomData(): string[] {
    return Array.from(this.joinedRooms);
  }
}

// Singleton instance for app-wide use
export const webSocketService = new WebSocketService();
